import React from 'react';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import ReplayIcon from '@mui/icons-material/Replay';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DoNotDisturbAltOutlinedIcon from '@mui/icons-material/DoNotDisturbAltOutlined';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

export interface SubStatusOption {
  code: number;
  label: string;
  icon: React.ReactElement;
  description?: string;
  danger?: boolean;
}

// (status, code) → label
const LABELS: Record<number, Record<number, string>> = {
  1: { 1: '再プレイ済', 7: '実績コンプリート', 8: '未クリア', 9: 'リタイア' },
  2: { 0: 'エンドレス', 1: '再プレイ中', 7: '実績コンプリート' },
  3: { 1: '再プレイ', 9: 'リタイア' },
  4: { 1: '再プレイ', 2: '優先度 高' },
  5: { 1: '購入予定' },
};

// (status, code) → description
const DESCRIPTIONS: Record<number, Record<number, string>> = {
  1: { 9: '面白くない,合わない等、"ネガティブ"な理由でプレイを諦めた状態' },
  3: { 9: '面白くない,合わない等、"ネガティブ"な理由でプレイを中断している状態' },
};

// status → 利用可能な code 一覧（表示順）
const AVAILABLE_CODES: Record<number, number[]> = {
  1: [1, 7, 8, 9],
  2: [1, 7],
  3: [1, 9],
  4: [1],
};

// 危険系（赤系で表示するもの）
const DANGER_CODES = new Set([9]);

export const getSubStatusIcon = (code: number): React.ReactElement => {
  switch (code) {
    case 0: return <AllInclusiveIcon fontSize="small" />;
    case 1: return <ReplayIcon fontSize="small" />;
    case 2: return <PriorityHighIcon fontSize="small" />;
    case 7: return <EmojiEventsIcon fontSize="small" />;
    case 8: return <RemoveCircleOutlineIcon fontSize="small" />;
    case 9: return <DoNotDisturbAltOutlinedIcon fontSize="small" />;
    default: return <ShoppingCartIcon fontSize="small" />;
  }
};

export const getSubStatusLabel = (status: number, code: number): string => {
  return LABELS[status]?.[code] ?? '';
};

export const getSubStatusOptions = (status: number | null | undefined): SubStatusOption[] => {
  if (!status) return [];
  const codes = AVAILABLE_CODES[status] ?? [];
  return codes.map(code => ({
    code,
    label: getSubStatusLabel(status, code),
    icon: getSubStatusIcon(code),
    description: DESCRIPTIONS[status]?.[code],
    danger: DANGER_CODES.has(code),
  }));
};

// 選択中のサブステータスをラベル化（status 文脈で解決）
export const getSelectedSubStatusLabels = (status: number, codes: number[]): string[] => {
  return codes.map(code => getSubStatusLabel(status, code)).filter(Boolean);
};

// 相互排他ルール（8 未クリア と 9 リタイア）を適用して toggle
export const toggleSubStatus = (current: number[], code: number): number[] => {
  let next: number[];
  if (code === 8 && current.includes(9)) {
    next = [...current.filter(s => s !== 9), 8];
  } else if (code === 9 && current.includes(8)) {
    next = [...current.filter(s => s !== 8), 9];
  } else if (current.includes(code)) {
    next = current.filter(s => s !== code);
  } else {
    next = [...current, code];
  }
  return next.sort((a, b) => a - b);
};
